'use client';
import { use, useState, useEffect } from 'react';
import Link from 'next/link';
import { notFound } from 'next/navigation';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import { createClient } from '@/lib/supabase/client';

interface InsightsPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  category: string | null;
  date: string | null;
  author: string;
  author_title: string;
  read_time: string | null;
  body: ArticleSection[] | null;
  published: boolean;
}

interface RelatedPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  category: string | null;
  date: string | null;
  read_time: string | null;
}

function ReadingProgressBar() {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const pct = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      setProgress(Math.min(100, Math.max(0, pct)));
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="fixed top-0 left-0 right-0 z-[100] h-[3px] bg-transparent pointer-events-none" role="progressbar" aria-valuenow={Math.round(progress)} aria-valuemin={0} aria-valuemax={100}>
      <div className="h-full bg-primary transition-[width] duration-75 ease-linear" style={{ width: `${progress}%` }} />
    </div>
  );
}

function renderSection(section: ArticleSection, index: number) {
  switch (section.type) {
    case 'heading':
      return (
        <h2 key={index} className="text-2xl md:text-3xl font-bold text-white mt-12 mb-5 tracking-tight">
          {section.content}
        </h2>
      );
    case 'subheading':
      return (
        <h3 key={index} className="text-xl font-bold text-white mt-8 mb-4 tracking-tight">
          {section.content}
        </h3>
      );
    case 'paragraph':
      return (
        <p key={index} className="text-gray-300 leading-relaxed mb-6 text-base md:text-lg">
          {section.content}
        </p>
      );
    case 'quote':
      return (
        <blockquote key={index} className="border-l-4 border-primary pl-6 my-10">
          <p className="text-xl md:text-2xl font-medium text-white leading-relaxed italic">
            &ldquo;{section.content}&rdquo;
          </p>
        </blockquote>
      );
    case 'list':
      return (
        <div key={index} className="my-8">
          {section.content && <p className="text-gray-300 mb-4 font-medium">{section.content}</p>}
          <ul className="space-y-3">
            {section.items?.map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="text-primary mt-1 flex-shrink-0">→</span>
                <span className="text-gray-300 leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      );
    default:
      return null;
  }
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch { return dateStr; }
}

interface Props {
  params: Promise<{ slug: string }>;
}

export default function ArticlePage({ params }: Props) {
  const { slug } = use(params);
  const [article, setArticle] = useState<InsightsPost | null>(null);
  const [related, setRelated] = useState<RelatedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFoundState, setNotFoundState] = useState(false);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('insights_posts')
          .select('*')
          .eq('slug', slug)
          .eq('published', true)
          .single();

        if (error || !data) {
          // Fallback to static data
          const staticArticle = getArticleBySlug(slug);
          if (!staticArticle) {
            setNotFoundState(true);
            return;
          }
          setArticle({
            id: staticArticle.slug,
            slug: staticArticle.slug,
            title: staticArticle.title,
            excerpt: staticArticle.excerpt,
            category: staticArticle.category,
            date: staticArticle.dateISO,
            author: staticArticle.author,
            author_title: staticArticle.authorTitle,
            read_time: staticArticle.readTime,
            body: staticArticle.body,
            published: true,
          });
          const staticRelated = getRelatedArticles(slug);
          setRelated(staticRelated.map(r => ({
            id: r.slug,
            slug: r.slug,
            title: r.title,
            excerpt: r.excerpt,
            category: r.category,
            date: r.dateISO,
            read_time: r.readTime,
          })));
          return;
        }

        setArticle(data);

        // Fetch related posts
        const { data: relatedData } = await supabase
          .from('insights_posts')
          .select('id, slug, title, excerpt, category, date, read_time')
          .eq('published', true)
          .neq('slug', slug)
          .limit(3);

        setRelated(relatedData || []);
      } catch {
        const staticArticle = getArticleBySlug(slug);
        if (!staticArticle) {
          setNotFoundState(true);
          return;
        }
        setArticle({
          id: staticArticle.slug,
          slug: staticArticle.slug,
          title: staticArticle.title,
          excerpt: staticArticle.excerpt,
          category: staticArticle.category,
          date: staticArticle.dateISO,
          author: staticArticle.author,
          author_title: staticArticle.authorTitle,
          read_time: staticArticle.readTime,
          body: staticArticle.body,
          published: true,
        });
        const staticRelated = getRelatedArticles(slug);
        setRelated(staticRelated.map(r => ({
          id: r.slug,
          slug: r.slug,
          title: r.title,
          excerpt: r.excerpt,
          category: r.category,
          date: r.dateISO,
          read_time: r.readTime,
        })));
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug]);

  if (notFoundState) {
    notFound();
  }

  if (loading) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-background">
          <section className="pt-40 pb-16 px-6 border-b border-border">
            <div className="max-w-[800px] mx-auto space-y-4">
              <div className="h-4 w-24 bg-surface-2 animate-pulse rounded" />
              <div className="h-12 w-3/4 bg-surface-2 animate-pulse rounded" />
              <div className="h-6 w-full bg-surface-2 animate-pulse rounded" />
              <div className="h-6 w-2/3 bg-surface-2 animate-pulse rounded" />
            </div>
          </section>
          <section className="py-16 px-6">
            <div className="max-w-[800px] mx-auto space-y-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-5 bg-surface-2 animate-pulse rounded" style={{ width: `${70 + Math.random() * 30}%` }} />
              ))}
            </div>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  if (!article) return null;

  const authorInitials = article.author
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <>
      <ReadingProgressBar />
      <Header />
      <main className="min-h-screen bg-background">
        {/* Hero */}
        <section className="pt-40 pb-16 px-6 border-b border-border">
          <div className="max-w-[800px] mx-auto">
            <div className="mb-6">
              <Link href="/insights" className="text-micro uppercase tracking-widest text-muted-foreground hover:text-white transition-colors inline-flex items-center gap-2">
                <span>←</span> All Insights
              </Link>
            </div>
            <div className="flex items-center gap-4 mb-6">
              <Badge variant={article.category === 'Framework' ? 'primary' : 'default'}>
                {article.category}
              </Badge>
              <span className="text-micro uppercase tracking-widest text-muted-foreground">
                {formatDate(article.date)}
              </span>
              <span className="text-micro text-muted-foreground">·</span>
              <span className="text-micro text-muted-foreground">{article.read_time}</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter leading-[0.95] text-white mb-8">
              {article.title}
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed mb-10">
              {article.excerpt}
            </p>
            {/* Author */}
            <div className="flex items-center gap-4 pt-8 border-t border-border">
              <div className="w-10 h-10 rounded-full bg-surface-2 border border-border flex items-center justify-center">
                <span className="text-sm font-bold text-primary">{authorInitials}</span>
              </div>
              <div>
                <div className="text-sm font-medium text-white">{article.author}</div>
                <div className="text-micro text-muted-foreground">{article.author_title}</div>
              </div>
            </div>
          </div>
        </section>

        {/* Article Body */}
        <section className="py-16 px-6">
          <div className="max-w-[800px] mx-auto">
            {(article.body || []).map((section, index) => renderSection(section, index))}
          </div>
        </section>

        {/* Related Articles */}
        {related.length > 0 && (
          <section className="py-20 px-6 border-t border-border bg-surface-1">
            <div className="max-w-[1200px] mx-auto">
              <div className="text-micro uppercase tracking-widest text-primary mb-4">Keep Reading</div>
              <h2 className="text-3xl font-bold text-white mb-12 tracking-tight">Related Articles</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {related.map(rel => (
                  <Link key={rel.slug} href={`/insights/${rel.slug}`} className="group block">
                    <div className="border border-border hover:border-primary transition-all duration-300 bg-background h-full flex flex-col">
                      <div className="p-8 flex-1 flex flex-col">
                        <div className="mb-4">
                          <Badge variant={rel.category === 'Framework' ? 'primary' : 'default'} className="text-xs mb-3">
                            {rel.category}
                          </Badge>
                          <div className="text-micro uppercase tracking-widest text-muted-foreground">
                            {formatDate(rel.date)}
                          </div>
                        </div>
                        <h3 className="text-lg font-bold text-white leading-tight mb-3 group-hover:text-primary transition-colors">
                          {rel.title}
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed mb-6 flex-1">
                          {rel.excerpt}
                        </p>
                        <div className="flex items-center gap-2 text-primary group-hover:gap-3 transition-all">
                          <span className="text-label uppercase tracking-widest">Read Article</span>
                          <span>→</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="py-24 px-6 border-t border-border">
          <div className="max-w-[700px] mx-auto text-center">
            <div className="text-micro uppercase tracking-widest text-primary mb-4">Next Step</div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tighter text-white mb-6">
              Ready to put this into practice?
            </h2>
            <p className="text-muted-foreground mb-10 text-lg">
              Book a consultation and we&apos;ll walk through how these frameworks apply to your specific situation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/book">
                <Button variant="primary" size="lg">Book Consultation</Button>
              </Link>
              <Link href="/insights">
                <Button variant="outline" size="lg">More Insights</Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
